/**
 * DDX typed API hooks — live inside apps/ddx only (AR-08).
 * Built on @axisnode/api-client; cancellation via TanStack Query (OA-05);
 * action + timing observability emitted here (OA-03).
 *
 * Covers ADR-007's 26 routes + ADR-008's two AI paths present in ddx_routes.py.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { getDdxClient, parseApiError, ApiError } from "./client";
import {
  forgetAssignment,
  listKnownAssignments,
  rememberAssignment,
} from "../domain/assignmentIndex";
import { emitObs } from "../obs";
import type {
  AdvanceStageResult,
  BoardResolution,
  Comment,
  Document,
  DocumentItem,
  DocumentVersion,
  Gap,
  GenerationSnapshotRow,
  PendingItem,
  Project,
  ProjectTemplate,
  PromoteGenerationResult,
  ResponsibilityAssignment,
} from "./types";

async function timedJson<T>(
  action: string,
  run: (signal: AbortSignal) => Promise<T>,
  signal?: AbortSignal,
): Promise<T> {
  const started = performance.now();
  try {
    const result = await run(signal ?? new AbortController().signal);
    emitObs("action", action, { ok: true });
    emitObs("timing", action, {
      ms: Math.round(performance.now() - started),
      ok: true,
    });
    return result;
  } catch (err) {
    const parsed = parseApiError(err);
    emitObs("action", action, {
      ok: false,
      status: parsed?.status,
    });
    emitObs("timing", action, {
      ms: Math.round(performance.now() - started),
      ok: false,
      status: parsed?.status,
    });
    throw err;
  }
}

function client() {
  return getDdxClient();
}

// ── Project (ADR-007 §3) ────────────────────────────────────────────────────

export function useCreateProjectTemplate(
  options?: UseMutationOptions<
    ProjectTemplate,
    Error,
    { name: string; stages: Array<{ name: string; order?: number }>; actor_id?: string }
  >,
) {
  return useMutation({
    mutationFn: (body) =>
      timedJson("create_project_template", (signal) =>
        client().requestJson<ProjectTemplate>({
          path: "/project/create_project_template",
          method: "POST",
          json: body,
          signal,
        }),
      ),
    ...options,
  });
}

export function useCreateProject(
  options?: UseMutationOptions<
    Project,
    Error,
    {
      name: string;
      template_ref: string;
      tenant_id?: string;
      actor_id?: string;
      auto_assign_package_code?: boolean;
    }
  >,
) {
  return useMutation({
    mutationFn: (body) =>
      timedJson("create_project", (signal) =>
        client().requestJson<Project>({
          path: "/project/create_project",
          method: "POST",
          json: {
            ...body,
            // Fix D — UI always asks the allocator; opt-out only via explicit false.
            auto_assign_package_code: body.auto_assign_package_code ?? true,
          },
          signal,
        }),
      ),
    ...options,
  });
}

export function useProject(
  projectId: string | undefined,
  options?: Omit<UseQueryOptions<Project, Error>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: ["ddx", "project", projectId],
    enabled: Boolean(projectId),
    queryFn: ({ signal }) =>
      timedJson(`GET /project/${projectId}`, (sig) =>
        client().requestJson<Project>({
          path: `/project/${projectId}`,
          method: "GET",
          signal: sig,
        }),
      signal),
    ...options,
  });
}

/**
 * Every Project visible to this tenant (tenant comes from the client's default
 * X-Tenant-Id header, set in client.ts — no param needed). Dashboard-navigation
 * infra added post-ADR-007; see the route's docstring in ddx_routes.py.
 */
export function useProjects(
  options?: Omit<UseQueryOptions<Project[], Error>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: ["ddx", "projects"],
    queryFn: ({ signal }) =>
      timedJson("GET /projects", (sig) =>
        client()
          .requestJson<{ projects?: Project[] }>({
            path: "/projects",
            method: "GET",
            signal: sig,
          })
          .then((r) => r.projects ?? []),
      signal),
    ...options,
  });
}

export function useAdvanceStage(
  options?: UseMutationOptions<
    AdvanceStageResult,
    Error,
    { projectId: string; actor_id?: string }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, actor_id }) => {
      const started = performance.now();
      try {
        const res = await client().request({
          path: `/project/${projectId}/advance_stage`,
          method: "POST",
          json: { actor_id },
        });
        const text = await res.text();
        let body: unknown = text;
        try {
          body = text ? JSON.parse(text) : {};
        } catch {
          /* keep */
        }
        if (
          body &&
          typeof body === "object" &&
          "detail" in (body as object)
        ) {
          body = (body as { detail: unknown }).detail;
        }
        emitObs("timing", "advance_stage", {
          ms: Math.round(performance.now() - started),
          status: res.status,
        });
        if (res.status === 202) {
          emitObs("action", "advance_stage", { ok: true, branch: "waiting" });
          return {
            kind: "waiting" as const,
            data: body as import("./types").AdvanceStageWaiting,
          };
        }
        if (res.status === 409) {
          emitObs("action", "advance_stage", { ok: true, branch: "blocked" });
          return {
            kind: "blocked" as const,
            data: body as import("./types").AdvanceStageBlocked,
          };
        }
        if (!res.ok) {
          emitObs("action", "advance_stage", { ok: false, status: res.status });
          throw new ApiError(res.status, text);
        }
        emitObs("action", "advance_stage", { ok: true, branch: "advanced" });
        return {
          kind: "advanced" as const,
          data: body as import("./types").AdvanceStageSuccess,
        };
      } catch (err) {
        emitObs("action", "advance_stage", { ok: false });
        throw err;
      }
    },
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ["ddx", "project", vars.projectId] });
    },
    ...options,
  });
}

export function useArchiveProject(
  options?: UseMutationOptions<
    Project,
    Error,
    { projectId: string; actor_id?: string }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, actor_id }) =>
      timedJson("archive_project", (signal) =>
        client().requestJson<Project>({
          path: `/project/${projectId}/archive`,
          method: "POST",
          json: { actor_id },
          signal,
        }),
      ),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ["ddx", "project", vars.projectId] });
    },
    ...options,
  });
}

/**
 * Infra, not an ADR-007 Capability (not in DDX_TYPED_HOOK_ROUTES) — creates
 * the project's evidence folder (transcripts/legacy) on the projects/ bind
 * mount. Real and immediate; landing in `main` happens on
 * ddx-projects-sync.timer's ~2min cadence, not synchronously with this call
 * — axisnode-mcp has no git binary or push credentials by design.
 */
export function useProvisionFolder(
  options?: UseMutationOptions<
    { slug: string; path: string; already_existed: boolean },
    Error,
    { projectId: string }
  >,
) {
  return useMutation({
    mutationFn: ({ projectId }) =>
      timedJson("provision_folder", (signal) =>
        client().requestJson({
          path: `/project/${projectId}/provision_folder`,
          method: "POST",
          json: {},
          signal,
        }),
      ),
    ...options,
  });
}

export type TimelineEntry = {
  kind: "DocumentVersion" | "ApprovalRecord" | "Comment" | "BoardResolution";
  id: string;
  occurred_at: string;
  actor_id?: string;
  document_id?: string;
  /** Which document this event touched — a timeline "subtitle" (absent for
   *  BoardResolution, the structural mechanism with no linked document). */
  document_type?: string;
  document_title?: string;
  version_label?: string;
  document_version_id?: string;
  decision?: string;
  body?: string;
  resolved?: boolean;
  subject?: string;
  status?: string;
};

/** Backend field is `entries`, not `events` — reporting_domain.get_project_timeline. */
export function useProjectTimeline(projectId: string | undefined) {
  return useQuery({
    queryKey: ["ddx", "timeline", projectId],
    enabled: Boolean(projectId),
    queryFn: ({ signal }) =>
      timedJson("get_project_timeline", (sig) =>
        client().requestJson<{ project_id?: string; entries?: TimelineEntry[] }>({
          path: `/project/${projectId}/timeline`,
          method: "GET",
          signal: sig,
        }),
      signal),
  });
}

export type DocumentMatrixRow = {
  document_id: string;
  document_type: string;
  title?: string;
  stage: string;
  lifecycle_status: string;
  gap_missing: unknown[];
  responsible_actors: string[];
};

export function useDocumentMatrix(projectId: string | undefined) {
  return useQuery({
    queryKey: ["ddx", "document-matrix", projectId],
    enabled: Boolean(projectId),
    queryFn: ({ signal }) =>
      timedJson("get_document_matrix", (sig) =>
        client().requestJson<{ project_id?: string; rows?: DocumentMatrixRow[] }>({
          path: `/project/${projectId}/document-matrix`,
          method: "GET",
          signal: sig,
        }),
      signal),
  });
}

/** null for Stage order 1 (nothing transitions into the start — axiomatic)
 *  and for any unreached Stage. */
export type StageTracklineDocument = {
  document_id: string;
  document_version_id: string;
  title?: string;
  document_type?: string;
};

export type StageTracklineEvidence = {
  transition_id: string;
  document_version_ids: string[];
  documents: StageTracklineDocument[];
  approval_record_ids: string[];
  board_resolution_id: string | null;
  occurred_at: string;
};

export type StageTracklinePoint = {
  stage_id: string;
  name: string;
  order: number;
  reached: boolean;
  evidence: StageTracklineEvidence | null;
};

export type StageTracklineGeneration = {
  generation: string;
  stages: StageTracklinePoint[];
};

/** OE-DDX-UI-002 / ADR-009 §8.4. A generation with no evidence of its own
 *  shows every Stage unreached — real behavior, not a loading/error state:
 *  iterating a document doesn't require advance_stage to fire again. */
export function useStageTrackline(projectId: string | undefined) {
  return useQuery({
    queryKey: ["ddx", "stage-trackline", projectId],
    enabled: Boolean(projectId),
    queryFn: ({ signal }) =>
      timedJson("get_stage_trackline", (sig) =>
        client().requestJson<{
          project_id?: string;
          current_stage_id?: string;
          generations?: StageTracklineGeneration[];
        }>({
          path: `/project/${projectId}/stage-trackline`,
          method: "GET",
          signal: sig,
        }),
      signal),
  });
}

/** OE-DDX-004 — Prototype-v0 -> MVP-v0.x -> Producción-V1 cycle promotion.
 *  Raw request (not requestJson), same pattern as useAdvanceStage, so the
 *  caller can branch on the 409 PENDING_ITEMS_OPEN body vs. success. */
export function usePromoteGeneration(
  options?: UseMutationOptions<
    PromoteGenerationResult,
    Error,
    { projectId: string; new_generation: string; actor_id?: string; carry_forward?: boolean }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, new_generation, actor_id, carry_forward }) => {
      const started = performance.now();
      try {
        const res = await client().request({
          path: `/project/${projectId}/promote_generation`,
          method: "POST",
          json: { new_generation, actor_id, carry_forward },
        });
        const text = await res.text();
        let body: unknown = text;
        try {
          body = text ? JSON.parse(text) : {};
        } catch {
          /* keep */
        }
        if (body && typeof body === "object" && "detail" in (body as object)) {
          body = (body as { detail: unknown }).detail;
        }
        emitObs("timing", "promote_generation", {
          ms: Math.round(performance.now() - started),
          status: res.status,
        });
        if (res.status === 409) {
          emitObs("action", "promote_generation", { ok: true, branch: "pendingItemsOpen" });
          return {
            kind: "pendingItemsOpen" as const,
            data: body as import("./types").PromoteGenerationBlocked,
          };
        }
        if (!res.ok) {
          emitObs("action", "promote_generation", { ok: false, status: res.status });
          throw new ApiError(res.status, text);
        }
        emitObs("action", "promote_generation", { ok: true, branch: "promoted" });
        return {
          kind: "promoted" as const,
          data: body as import("./types").PromoteGenerationSuccess,
        };
      } catch (err) {
        emitObs("action", "promote_generation", { ok: false });
        throw err;
      }
    },
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ["ddx", "project", vars.projectId] });
      void qc.invalidateQueries({ queryKey: ["ddx", "stage-trackline", vars.projectId] });
      void qc.invalidateQueries({ queryKey: ["ddx", "pending-items", vars.projectId] });
    },
    ...options,
  });
}

export function usePendingItems(projectId: string | undefined, status?: string) {
  return useQuery({
    queryKey: ["ddx", "pending-items", projectId, status],
    enabled: Boolean(projectId),
    queryFn: ({ signal }) =>
      timedJson("list_pending_items", (sig) =>
        client().requestJson<{ pending_items?: PendingItem[] }>({
          path: `/project/${projectId}/pending-items${status ? `?status=${status}` : ""}`,
          method: "GET",
          signal: sig,
        }),
      signal),
  });
}

export function useCreatePendingItem(
  options?: UseMutationOptions<
    PendingItem,
    Error,
    { projectId: string; title: string; description?: string; actor_id?: string }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, title, description, actor_id }) =>
      timedJson("create_pending_item", (signal) =>
        client().requestJson<PendingItem>({
          path: `/project/${projectId}/pending-items`,
          method: "POST",
          json: { title, description, actor_id },
          signal,
        }),
      ),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ["ddx", "pending-items", vars.projectId] });
    },
    ...options,
  });
}

export function useResolvePendingItem(
  options?: UseMutationOptions<
    PendingItem,
    Error,
    { pendingId: string; projectId: string; actor_id?: string }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pendingId, actor_id }) =>
      timedJson("resolve_pending_item", (signal) =>
        client().requestJson<PendingItem>({
          path: `/pending-item/${pendingId}/resolve`,
          method: "POST",
          json: { actor_id },
          signal,
        }),
      ),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ["ddx", "pending-items", vars.projectId] });
    },
    ...options,
  });
}

/** OE-DDX-004 — the document set "as of" a generation, for the trackline's
 *  generation selector ("selecting a version deploys the documents"). */
export function useGenerationSnapshot(
  projectId: string | undefined,
  generation: string | undefined,
) {
  return useQuery({
    queryKey: ["ddx", "generation-snapshot", projectId, generation],
    enabled: Boolean(projectId) && Boolean(generation),
    queryFn: ({ signal }) =>
      timedJson("get_generation_snapshot", (sig) =>
        client().requestJson<{ documents?: GenerationSnapshotRow[] }>({
          path: `/project/${projectId}/generation-snapshot?generation=${encodeURIComponent(generation ?? "")}`,
          method: "GET",
          signal: sig,
        }),
      signal),
  });
}

// ── Document (ADR-007 §4 + ADR-008) ─────────────────────────────────────────

/** Route reads template_id/template_ref — NOT project_id (document_types are per-template). */
export function useRegisterDocumentType(
  options?: UseMutationOptions<
    { type_id: string; [k: string]: unknown },
    Error,
    { name: string; templateId: string; actor_id?: string }
  >,
) {
  return useMutation({
    mutationFn: ({ name, templateId, actor_id }) =>
      timedJson("register_document_type", (signal) =>
        client().requestJson({
          path: "/document/register_document_type",
          method: "POST",
          json: { name, template_id: templateId, actor_id },
          signal,
        }),
      ),
    ...options,
  });
}

/** Route reads type_id/type_ref — NOT document_type_id (that name is Responsibility's field). */
export function useCreateDocument(
  options?: UseMutationOptions<
    Document,
    Error,
    {
      project_id: string;
      type_id: string;
      title?: string;
      actor_id?: string;
      tenant_id?: string;
    }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ project_id, type_id, title, actor_id, tenant_id }) =>
      timedJson("create_document", (signal) =>
        client().requestJson<Document>({
          path: "/document/create_document",
          method: "POST",
          json: { project_id, type_id, title, actor_id, tenant_id },
          signal,
        }),
      ),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({
        queryKey: ["ddx", "document-matrix", vars.project_id],
      });
    },
    ...options,
  });
}

export function useDocument(documentId: string | undefined) {
  return useQuery({
    queryKey: ["ddx", "document", documentId],
    enabled: Boolean(documentId),
    queryFn: ({ signal }) =>
      timedJson(`GET /document/${documentId}`, (sig) =>
        client().requestJson<Document>({
          path: `/document/${documentId}`,
          method: "GET",
          signal: sig,
        }),
      signal),
  });
}

export function useDocumentVersions(documentId: string | undefined) {
  return useQuery({
    queryKey: ["ddx", "versions", documentId],
    enabled: Boolean(documentId),
    queryFn: ({ signal }) =>
      timedJson("list_versions", (sig) =>
        client().requestJson<{ versions: DocumentVersion[] }>({
          path: `/document/${documentId}/versions`,
          method: "GET",
          signal: sig,
        }),
      signal),
  });
}

export function useSubmitVersion(
  options?: UseMutationOptions<
    DocumentVersion,
    Error,
    {
      documentId: string;
      content_ref: string;
      version_label: string;
      author_actor_id?: string;
      actor_id?: string;
    }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, content_ref, version_label, author_actor_id, actor_id }) =>
      timedJson("submit_document_version", (signal) =>
        client().requestJson<DocumentVersion>({
          path: `/document/${documentId}/submit_version`,
          method: "POST",
          json: { content_ref, version_label, author_actor_id, actor_id },
          signal,
        }),
      ),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ["ddx", "document", vars.documentId] });
      void qc.invalidateQueries({ queryKey: ["ddx", "versions", vars.documentId] });
    },
    ...options,
  });
}

export function useRequestDraftAssistance(
  options?: UseMutationOptions<
    DocumentVersion,
    Error,
    { documentId: string; prompt?: string; actor_id?: string }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, prompt, actor_id }) =>
      timedJson("request_draft_assistance", (signal) =>
        client().requestJson<DocumentVersion>({
          path: `/document/${documentId}/request_draft_assistance`,
          method: "POST",
          json: { prompt, actor_id },
          signal,
        }),
      ),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ["ddx", "document", vars.documentId] });
      void qc.invalidateQueries({ queryKey: ["ddx", "versions", vars.documentId] });
    },
    ...options,
  });
}

/** Route reads `prompt` — NOT `question`. */
export function useRequestBoardOpinion(
  options?: UseMutationOptions<
    Comment,
    Error,
    { documentId: string; prompt?: string; actor_id?: string }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, prompt, actor_id }) =>
      timedJson("request_board_opinion", (signal) =>
        client().requestJson<Comment>({
          path: `/document/${documentId}/request_board_opinion`,
          method: "POST",
          json: { prompt, actor_id },
          signal,
        }),
      ),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ["ddx", "comments", vars.documentId] });
    },
    ...options,
  });
}

export function useDeprecateDocument(
  options?: UseMutationOptions<
    Document,
    Error,
    { documentId: string; actor_id?: string }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, actor_id }) =>
      timedJson("deprecate_document", (signal) =>
        client().requestJson<Document>({
          path: `/document/${documentId}/deprecate`,
          method: "POST",
          json: { actor_id },
          signal,
        }),
      ),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ["ddx", "document", vars.documentId] });
    },
    ...options,
  });
}

// ── Document Items (post-ADR-007 infra) ─────────────────────────────────────
// Numbered, reorderable, editable line-items inside a single document — see
// ddx_routes.py's "Document Items" section for the backend-side rationale.

export function useDocumentItems(documentId: string | undefined) {
  return useQuery({
    queryKey: ["ddx", "items", documentId],
    enabled: Boolean(documentId),
    queryFn: ({ signal }) =>
      timedJson("GET /document/{id}/items", (sig) =>
        client().requestJson<{ items: DocumentItem[] }>({
          path: `/document/${documentId}/items`,
          method: "GET",
          signal: sig,
        }),
      signal),
  });
}

export function useAddItem(
  options?: UseMutationOptions<
    DocumentItem,
    Error,
    { documentId: string; content: string; actor_id?: string }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, content, actor_id }) =>
      timedJson("POST /document/{id}/items", (signal) =>
        client().requestJson<DocumentItem>({
          path: `/document/${documentId}/items`,
          method: "POST",
          json: { content, actor_id },
          signal,
        }),
      ),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ["ddx", "items", vars.documentId] });
    },
    ...options,
  });
}

export function useUpdateItem(
  options?: UseMutationOptions<
    DocumentItem,
    Error,
    { itemId: string; documentId: string; content: string; actor_id?: string }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, content, actor_id }) =>
      timedJson("PATCH /document-item/{id}", (signal) =>
        client().requestJson<DocumentItem>({
          path: `/document-item/${itemId}`,
          method: "PATCH",
          json: { content, actor_id },
          signal,
        }),
      ),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ["ddx", "items", vars.documentId] });
    },
    ...options,
  });
}

export function useDeleteItem(
  options?: UseMutationOptions<
    { item_id: string; deleted: boolean },
    Error,
    { itemId: string; documentId: string; actor_id?: string }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, actor_id }) =>
      timedJson("DELETE /document-item/{id}", (signal) =>
        client().requestJson<{ item_id: string; deleted: boolean }>({
          path: `/document-item/${itemId}`,
          method: "DELETE",
          json: { actor_id },
          signal,
        }),
      ),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ["ddx", "items", vars.documentId] });
    },
    ...options,
  });
}

export function useReorderItems(
  options?: UseMutationOptions<
    { document_id: string; items: DocumentItem[] },
    Error,
    { documentId: string; itemIds: string[]; actor_id?: string }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, itemIds, actor_id }) =>
      timedJson("POST /document/{id}/items/reorder", (signal) =>
        client().requestJson<{ document_id: string; items: DocumentItem[] }>({
          path: `/document/${documentId}/items/reorder`,
          method: "POST",
          json: { item_ids: itemIds, actor_id },
          signal,
        }),
      ),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ["ddx", "items", vars.documentId] });
    },
    ...options,
  });
}

export function useExplodeVersionIntoItems(
  options?: UseMutationOptions<
    { document_id: string; version_id: string; items: DocumentItem[]; count: number },
    Error,
    { documentId: string; versionId?: string; actor_id?: string }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, versionId, actor_id }) =>
      timedJson("POST /document/{id}/items/explode", (signal) =>
        client().requestJson<{
          document_id: string;
          version_id: string;
          items: DocumentItem[];
          count: number;
        }>({
          path: `/document/${documentId}/items/explode`,
          method: "POST",
          json: { version_id: versionId, actor_id },
          signal,
        }),
      ),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ["ddx", "items", vars.documentId] });
    },
    ...options,
  });
}

// ── Responsibility (ADR-007 §5) ─────────────────────────────────────────────

export function useAssignResponsibility(
  options?: UseMutationOptions<
    ResponsibilityAssignment,
    Error,
    {
      project_id: string;
      document_type_id: string;
      role_id: string;
      actor_id: string;
      invoking_actor_id?: string;
    }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      timedJson("assign_responsibility", (signal) =>
        client().requestJson<ResponsibilityAssignment>({
          path: "/responsibility/assign_responsibility",
          method: "POST",
          json: body,
          signal,
        }),
      ),
    onSuccess: (row, vars) => {
      // ADR-DEV-DDX-UI-002 — remember for Team/§8; no list Capability exists.
      rememberAssignment({
        ...row,
        project_id: row.project_id ?? vars.project_id,
        document_type_id: row.document_type_id ?? vars.document_type_id,
        role_id: row.role_id ?? vars.role_id,
        actor_id: row.actor_id ?? vars.actor_id,
      });
      void qc.invalidateQueries({
        queryKey: ["ddx", "assignments", vars.project_id],
      });
    },
    ...options,
  });
}

export function useRevokeResponsibility(
  options?: UseMutationOptions<
    unknown,
    Error,
    { assignmentId: string; projectId?: string; actor_id?: string }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assignmentId, actor_id }) =>
      timedJson("revoke_responsibility", (signal) =>
        client().requestJson({
          path: `/responsibility/${assignmentId}/revoke`,
          method: "POST",
          json: { actor_id },
          signal,
        }),
      ),
    onSuccess: (_r, vars) => {
      if (vars.projectId) {
        forgetAssignment(vars.projectId, vars.assignmentId);
        void qc.invalidateQueries({
          queryKey: ["ddx", "assignments", vars.projectId],
        });
      }
    },
    ...options,
  });
}

/**
 * ADR-DEV-DDX-UI-002 — no GET list for assignments.
 * Merge rare embedded project.assignments[] with the session navigation index
 * populated from assign_responsibility responses.
 */
export function useProjectAssignments(projectId: string | undefined) {
  return useQuery({
    queryKey: ["ddx", "assignments", projectId],
    enabled: Boolean(projectId),
    queryFn: async ({ signal }) => {
      const project = await timedJson("assignments_via_project", (sig) =>
        client().requestJson<
          Project & { assignments?: ResponsibilityAssignment[] }
        >({
          path: `/project/${projectId}`,
          method: "GET",
          signal: sig,
        }),
      signal);
      const embedded = Array.isArray(project.assignments)
        ? project.assignments
        : [];
      const known = listKnownAssignments(projectId!);
      const byId = new Map<string, ResponsibilityAssignment>();
      for (const a of [...embedded, ...known]) {
        if (a?.assignment_id) byId.set(a.assignment_id, a);
      }
      return Array.from(byId.values());
    },
  });
}

// ── Review (ADR-007 §6) ─────────────────────────────────────────────────────

/**
 * Route reads target_type/target_id(_ref)/required_roles — none of which the
 * previous shape sent (it always 400'd: required_roles defaulted to [], and
 * define_review_requirement rejects an empty list outright).
 */
export function useDefineReviewRequirement(
  options?: UseMutationOptions<
    unknown,
    Error,
    {
      targetType: "DocumentType" | "Stage";
      targetId: string;
      requiredRoles: string[];
      min_approvals?: number;
      actor_id?: string;
    }
  >,
) {
  return useMutation({
    mutationFn: ({ targetType, targetId, requiredRoles, min_approvals, actor_id }) =>
      timedJson("define_review_requirement", (signal) =>
        client().requestJson({
          path: "/review/define_review_requirement",
          method: "POST",
          json: {
            target_type: targetType,
            target_id: targetId,
            required_roles: requiredRoles,
            min_approvals,
            actor_id,
          },
          signal,
        }),
      ),
    ...options,
  });
}

export function useApproveVersion(
  options?: UseMutationOptions<
    unknown,
    Error,
    { versionId: string; actor_id: string; documentId?: string }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ versionId, actor_id }) =>
      timedJson("approve_document_version", (signal) =>
        client().requestJson({
          path: `/document-version/${versionId}/approve`,
          method: "POST",
          json: { actor_id },
          signal,
        }),
      ),
    onSuccess: (_r, vars) => {
      if (vars.documentId) {
        void qc.invalidateQueries({ queryKey: ["ddx", "document", vars.documentId] });
        void qc.invalidateQueries({ queryKey: ["ddx", "versions", vars.documentId] });
      }
    },
    ...options,
  });
}

/** Route reads `notes` — NOT `reason`. */
export function useRequestChanges(
  options?: UseMutationOptions<
    unknown,
    Error,
    { versionId: string; actor_id: string; notes?: string; documentId?: string }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ versionId, actor_id, notes }) =>
      timedJson("request_changes", (signal) =>
        client().requestJson({
          path: `/document-version/${versionId}/request_changes`,
          method: "POST",
          json: { actor_id, notes },
          signal,
        }),
      ),
    onSuccess: (_r, vars) => {
      if (vars.documentId) {
        void qc.invalidateQueries({ queryKey: ["ddx", "document", vars.documentId] });
        void qc.invalidateQueries({ queryKey: ["ddx", "versions", vars.documentId] });
      }
    },
    ...options,
  });
}

/** Route reads `notes` — NOT `reason`. */
export function useRejectVersion(
  options?: UseMutationOptions<
    unknown,
    Error,
    { versionId: string; actor_id: string; notes?: string; documentId?: string }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ versionId, actor_id, notes }) =>
      timedJson("reject_document_version", (signal) =>
        client().requestJson({
          path: `/document-version/${versionId}/reject`,
          method: "POST",
          json: { actor_id, notes },
          signal,
        }),
      ),
    onSuccess: (_r, vars) => {
      if (vars.documentId) {
        void qc.invalidateQueries({ queryKey: ["ddx", "document", vars.documentId] });
        void qc.invalidateQueries({ queryKey: ["ddx", "versions", vars.documentId] });
      }
    },
    ...options,
  });
}

/**
 * Route reads requirement_id(_ref)/current_state_ref — neither of which the
 * previous shape sent (unused by any screen today; fixed for correctness).
 */
export function useEvaluateReadiness(
  options?: UseMutationOptions<
    unknown,
    Error,
    {
      requirementId: string;
      currentStateRef: string;
      project_id?: string;
      document_type_id?: string;
      actor_id?: string;
    }
  >,
) {
  return useMutation({
    mutationFn: ({ requirementId, currentStateRef, project_id, document_type_id, actor_id }) =>
      timedJson("evaluate_readiness", (signal) =>
        client().requestJson({
          path: "/review/evaluate_readiness",
          method: "POST",
          json: {
            requirement_id: requirementId,
            current_state_ref: currentStateRef,
            project_id,
            document_type_id,
            actor_id,
          },
          signal,
        }),
      ),
    ...options,
  });
}

/**
 * Route reads subject/status(enum)/supersedes_resolution_id — the previous
 * shape sent `decision` (never read) and never sent the required `status`
 * enum at all (board_resolutions.status is NOT NULL with a CHECK constraint —
 * every call would have failed).
 */
export function useIssueBoardResolution(
  options?: UseMutationOptions<
    BoardResolution,
    Error,
    {
      project_id: string;
      subject: string;
      status: "Approved" | "ApprovedWithConditions" | "Rejected";
      conditions?: string;
      supersedesResolutionId?: string;
      actor_id?: string;
    }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ project_id, subject, status, conditions, supersedesResolutionId, actor_id }) =>
      timedJson("issue_board_resolution", (signal) =>
        client().requestJson<BoardResolution>({
          path: "/review/issue_board_resolution",
          method: "POST",
          json: {
            project_id,
            subject,
            status,
            conditions,
            supersedes_resolution_id: supersedesResolutionId,
            actor_id,
          },
          signal,
        }),
      ),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ["ddx", "project", vars.project_id] });
      void qc.invalidateQueries({
        queryKey: ["ddx", "resolutions", vars.project_id],
      });
    },
    ...options,
  });
}

export function useGap(gapId: string | undefined) {
  return useQuery({
    queryKey: ["ddx", "gap", gapId],
    enabled: Boolean(gapId),
    queryFn: ({ signal }) =>
      timedJson(`GET /gap/${gapId}`, (sig) =>
        client().requestJson<Gap>({
          path: `/gap/${gapId}`,
          method: "GET",
          signal: sig,
        }),
      signal),
  });
}

// ── Discussion (ADR-007 §7) ─────────────────────────────────────────────────

export function useComments(documentId: string | undefined) {
  return useQuery({
    queryKey: ["ddx", "comments", documentId],
    enabled: Boolean(documentId),
    queryFn: ({ signal }) =>
      timedJson("list_comments", (sig) =>
        client().requestJson<{ comments: Comment[] }>({
          path: `/document/${documentId}/comments`,
          method: "GET",
          signal: sig,
        }),
      signal),
  });
}

export function usePostComment(
  options?: UseMutationOptions<
    Comment,
    Error,
    { documentId: string; body: string; actor_id?: string }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, body, actor_id }) =>
      timedJson("post_comment", (signal) =>
        client().requestJson<Comment>({
          path: `/document/${documentId}/comments`,
          method: "POST",
          json: { body, content: body, actor_id },
          signal,
        }),
      ),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ["ddx", "comments", vars.documentId] });
    },
    ...options,
  });
}

export function useResolveComment(
  options?: UseMutationOptions<
    unknown,
    Error,
    { commentId: string; documentId?: string; actor_id?: string }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, actor_id }) =>
      timedJson("resolve_comment", (signal) =>
        client().requestJson({
          path: `/comment/${commentId}/resolve`,
          method: "POST",
          json: { actor_id },
          signal,
        }),
      ),
    onSuccess: (_r, vars) => {
      if (vars.documentId) {
        void qc.invalidateQueries({
          queryKey: ["ddx", "comments", vars.documentId],
        });
      }
    },
    ...options,
  });
}

export function useEscalateComment(
  options?: UseMutationOptions<
    unknown,
    Error,
    { commentId: string; documentId?: string; actor_id?: string }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, actor_id }) =>
      timedJson("escalate_comment_to_review", (signal) =>
        client().requestJson({
          path: `/comment/${commentId}/escalate`,
          method: "POST",
          json: { actor_id },
          signal,
        }),
      ),
    onSuccess: (_r, vars) => {
      if (vars.documentId) {
        void qc.invalidateQueries({
          queryKey: ["ddx", "comments", vars.documentId],
        });
      }
    },
    ...options,
  });
}

/** Catalog of every ADR-007 + ADR-008 route wired by this module (AR-08 proof). */
export const DDX_TYPED_HOOK_ROUTES = [
  "POST /project/create_project_template",
  "POST /project/create_project",
  "GET /projects",
  "POST /project/{id}/advance_stage",
  "POST /project/{id}/archive",
  "GET /project/{id}",
  "GET /project/{id}/timeline",
  "GET /project/{id}/document-matrix",
  "POST /document/register_document_type",
  "POST /document/create_document",
  "POST /document/{id}/submit_version",
  "POST /document/{id}/request_draft_assistance",
  "POST /document/{id}/request_board_opinion",
  "POST /document/{id}/deprecate",
  "GET /document/{id}",
  "GET /document/{id}/versions",
  "POST /document/{id}/comments",
  "GET /document/{id}/comments",
  "GET /document/{id}/items",
  "POST /document/{id}/items",
  "PATCH /document-item/{id}",
  "DELETE /document-item/{id}",
  "POST /document/{id}/items/reorder",
  "POST /document/{id}/items/explode",
  "POST /responsibility/assign_responsibility",
  "POST /responsibility/{id}/revoke",
  "POST /review/define_review_requirement",
  "POST /document-version/{id}/approve",
  "POST /document-version/{id}/request_changes",
  "POST /document-version/{id}/reject",
  "POST /review/evaluate_readiness",
  "POST /review/issue_board_resolution",
  "GET /gap/{id}",
  "POST /comment/{id}/resolve",
  "POST /comment/{id}/escalate",
] as const;
